/**
 * Binary Formats Page
 *
 * Main page for managing binary format definitions.
 * Lists all formats with filtering and provides access to the editor.
 */

import React, { useEffect, useState } from 'react';
import { Plus, Download, Upload, FileCode, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BinaryFormatList } from '@/components/binary-formats/BinaryFormatList';
import { BinaryFormatEditorModal } from '@/components/binary-formats/BinaryFormatEditorModal';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import type { FormatStatus } from '@/types/binaryFormat';

export const BinaryFormatsPage: React.FC = () => {
  const {
    formats,
    systemTemplates,
    isLoading,
    formatsLoaded,
    error,
    fetchFormats,
    fetchSystemTemplates,
    openEditor,
  } = useBinaryFormatStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FormatStatus | 'all'>('all');

  // Fetch formats on mount
  useEffect(() => {
    if (!formatsLoaded) {
      fetchFormats();
      fetchSystemTemplates();
    }
  }, [formatsLoaded, fetchFormats, fetchSystemTemplates]);

  // Filter formats based on search and status
  const filteredFormats = formats.filter((format) => {
    // Status filter
    if (statusFilter !== 'all' && format.status !== statusFilter) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = format.name.toLowerCase().includes(query);
      const matchesDescription = format.description?.toLowerCase().includes(query);
      const matchesTags = format.tags?.some((tag) => tag.toLowerCase().includes(query));
      if (!matchesName && !matchesDescription && !matchesTags) {
        return false;
      }
    }

    return true;
  });

  const handleRefresh = () => {
    fetchFormats();
  };

  const handleNewFormat = () => {
    openEditor();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCode className="h-6 w-6" />
            Binary Format Definitions
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage binary payload parsers for IoT devices
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleNewFormat}>
            <Plus className="h-4 w-4 mr-2" />
            New Format
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 p-4 border-b bg-muted/30">
        <Input
          placeholder="Search formats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as FormatStatus | 'all')}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Refresh'
          )}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-destructive/10 text-destructive rounded-md">
          {error}
        </div>
      )}

      {/* Format List */}
      <div className="flex-1 overflow-auto p-4">
        <BinaryFormatList
          formats={filteredFormats}
          systemTemplates={systemTemplates}
          isLoading={isLoading}
        />
      </div>

      {/* Editor Modal */}
      <BinaryFormatEditorModal />
    </div>
  );
};

export default BinaryFormatsPage;
